import React, { Component, PropTypes } from 'react';
import { browserHistory } from 'react-router';
import classnames from 'classnames';
import moment from 'moment';
import partial from 'lodash/partial';
import Tooltip from 'react-tooltip';
import { GROUP_ICON, GROUP_CUSTOM_DESCRIPTION } from 'registration/creative/components/work_samples';
import { REJECTED_SAMPLES_RESUBMISSION_WINDOW } from 'registration/constants';
import { reopenRegistrationRequest } from 'registration/creative/ducks';

export default class WorkSamples extends Component {
  constructor(props) {
    super(props);

    this.state = { showingSamples: {} };

    this.renderGroup = this.renderGroup.bind(this);
    this.toggleSamples = this.toggleSamples.bind(this);
    this.goToRegistration = this.goToRegistration.bind(this);
  }

  goToRegistration() {
    const { idVerificationStatus, user, fetchIdPhotoIfNeeded, setBannerError } = this.props;
    const continueRegistration = () => browserHistory.push('/register/continue/');

    /* re-open registration before continuing */
    if (idVerificationStatus === "closed") {
      reopenRegistrationRequest(user.id).then(
        () => fetchIdPhotoIfNeeded(true)
      ).then(
        () => continueRegistration()
      ).catch(() => (
        setBannerError('Error re-opening registration', 'Please try again.')
      ));
    } else {
      continueRegistration();
    }
  }

  componentDidMount() {
    const { fetchSampleGroups, fetchWritingSamples, fetchImageSamples } = this.props;

    fetchSampleGroups();
    fetchWritingSamples();
    fetchImageSamples();
  }

  toggleSamples(group) {
    const { showingSamples } = this.state;

    this.setState({
      showingSamples: {
        ...showingSamples,
        [group.id]: showingSamples[group.id]? false : true
      }
    });
  }

  renderSamples(group) {
    const samples = this.props.samples[group.id];
    const sampleType = group.subcategories[0].entry_type;

    return (
      <div className="samples">
        { sampleType === 'image'?
        <div className="images">
          { samples.sample_1?
          <div>
            { Object.values(samples).map((s, i) => <img key={i} src={s} />) }
          </div> :
          <div>
            <strong>Legacy creative</strong><br/>
            <strong>No image samples</strong>
          </div> }
        </div> :
        <div>
          { samples.favorite_company_name?
          <div>
            <h4>
              1. What is your favorite company name and why do you think it's a strong name?
            </h4>
            <p>{ samples.favorite_company_name }</p>

            <h4>
              2. Describe your experience naming companies and/or products.
            </h4>
            <p>{ samples.naming_experience }</p>

            <h4>
              3. Describe your process in helping clients name companies or products.
            </h4>
            <p>{ samples.naming_process }</p>

            <h4>
              4. Why do you want to work on crowdspring in naming projects?
            </h4>
            <p>{ samples.why_crowdspring }</p>

            <h4>
              5. What is your favorite tagline and why do you think it's a strong tagline?
            </h4>
            <p>{ samples.favorite_tagline }</p>
          </div> :
          <div>
            <strong>Legacy creative</strong><br/>
            <strong>No writing samples</strong>
          </div> }
        </div> }
      </div>
    );
  }

  renderSamplesToggle(group) {
    const showingSamples = this.state.showingSamples[group.id];

    return (
      <button className="btn btn-text btn-md" onClick={partial(this.toggleSamples, group)}>
        { showingSamples? "Hide" : "View" } submitted samples
        {' '}<i className={`fa fa-angle-${showingSamples? 'up' : 'down'}`} />
      </button>
    );
  }

  renderStatusInfo(group) {
    const { samplesMeta } = this.props;
    const meta = samplesMeta[group.id];

    if (!meta || meta.approved) {
      return null;
    }

    if (meta.approved === false) {
      const resubmissionWindowEnd = meta.processed_at? moment(meta.processed_at).add(
        REJECTED_SAMPLES_RESUBMISSION_WINDOW, 'months'
      ) : null;


      return (
        <div className="status-info">
          { !!meta.last_submitted_at &&
          <p>
            You previously requested to work in this category on
            {' '}{ moment(meta.last_submitted_at).format('MMM D, YYYY') }.
          </p> }

          { !!meta.processed_at &&
          <p>
            Your request was denied on { moment(meta.processed_at).format('MMM D, YYYY') }.
          </p> }

          { !resubmissionWindowEnd || moment().isAfter(resubmissionWindowEnd) ?
          <p className="text-warning">
            You may again apply to work in this category since more
            than { REJECTED_SAMPLES_RESUBMISSION_WINDOW } have passed. You must
            submit <strong>new</strong> samples which show your best work.
          </p> :
          <p className="text-danger">
            You may again apply to work in this category <strong>after{' '}
            { resubmissionWindowEnd.format('MMM D, YYYY') }</strong>.
          </p> }

          { this.renderSamplesToggle(group) }
        </div>
      );
    }

    if (meta.resubmission_requested) {
      return (
        <div className="status-info">
          { !!meta.last_submitted_at &&
          <p>
            You previously requested to work in this category on
            {' '}{ moment(meta.last_submitted_at).format('MMM D, YYYY') }.
          </p> }

          <p className="text-warning">
            Your samples were not approved but you may resubmit <strong>new</strong> samples
            that show your best work in this category.
          </p>

          { this.renderSamplesToggle(group) }
        </div>
      );
    }

    return null;
  }

  renderDescription(group) {
    const customDescription = GROUP_CUSTOM_DESCRIPTION[group.name];
    const hasManySubcats = group.subcategories.length > 5;

    if (customDescription) {
      return <p className="custom-description">{ customDescription }</p>;
    }

    return (
      <div className="sub-categories">
        <h5>Includes the following sub-categories:</h5>
        <div className={classnames("row", {
          "small-up-2": hasManySubcats,
          "small-up-1": !hasManySubcats
        })}>
          { group.subcategories.map((subcat) => (
          <div key={subcat.id} className="subcat columns">
            { subcat.name }
          </div>
          )) }
        </div>
      </div>
    );
  }

  renderGroup(group, opts={}) {
    const { active } = opts;
    const showingSamples = this.state.showingSamples[group.id];
    const iconName = GROUP_ICON[group.name];
    const icon = require(`assets/images/icons/pap/PAP_cat_icon_${iconName}.svg`);
    const statusInfo = this.renderStatusInfo(group);
    const description = this.renderDescription(group);

    return (
      <div key={group.id} className="row group align-middle">
        <div className="columns medium-5 large-4 small-16">
          <div className={classnames("tile", { active })} data-tip={true}
              data-for={`tooltip-${group.id}`}>
            <i className="icon" dangerouslySetInnerHTML={{ __html: icon }}/>
            <h4>{ group.name }</h4>
          </div>
          { !!statusInfo &&
          <Tooltip class="tooltip" effect="solid" place="bottom" id={`tooltip-${group.id}`}>
            { description }
          </Tooltip> }
        </div>
        <div className="columns medium-11 large-12 small-16">
          { statusInfo? statusInfo : description }

          { showingSamples && this.renderSamples(group) }
        </div>
      </div>
    );
  }

  renderApproved() {
    const { groups, samplesMeta, user } = this.props;
    const approvedGroups = groups.filter(g => samplesMeta[g.id] && samplesMeta[g.id].approved);
    let projectTypes = ["Silver", "Gold"];
    if (user.is_elite) {
      projectTypes = projectTypes.concat("Elite");
    }
    if (user.is_platinum) {
      projectTypes = projectTypes.concat("Platinum");
    }

    if (approvedGroups.length === 0) {
      return null;
    }

    return (
      <div className="approved-groups">
        <h3>You are approved to work in these categories:</h3>
        { user.reputation > 0 &&
        <p>
          Your current reputation is <strong>{ user.reputation }</strong>.
          You are approved to work in
          { projectTypes.map((t, i) => {
            return (
              <span key={i}>
                <strong> { t }</strong>
                { i === projectTypes.length - 2 && <span> and</span> }
                { i < projectTypes.length - 2 && <span>,</span> }
              </span>
            );
          }) }
          <br className="hide-for-small-only" /> projects in the following categories:
        </p> }

        <div className="groups">
          { approvedGroups.map(g => this.renderGroup(g, { active: true })) }
        </div>
      </div>
    );
  }

  renderNonApproved() {
    const { groups, samplesMeta, idVerificationStatus } = this.props;
    const nonApprovedGroups = groups.filter(g => !samplesMeta[g.id] || !samplesMeta[g.id].approved);

    if (nonApprovedGroups.length === 0) {
      return null;
    }

    return (
      <div className="non-approved-groups">
        <h3>You are <u>not</u> approved to work in these categories:</h3>
        <p>
          We qualify people in <strong>every</strong> category. To qualify
          to work in a category, you can submit samples <br className="hide-for-small-only" />
          from <strong>any sub-category listed for that category</strong>. You can add
          additional categories now or later.
        </p>
        <div className="groups">
          { nonApprovedGroups.map(g => this.renderGroup(g, {})) }
        </div>
        { ['closed', 'waiting_for_creative'].includes(idVerificationStatus) &&
        <button className="btn btn-tertiary btn-participate" onClick={this.goToRegistration}>
          I want to work in these additional categories
        </button> }
      </div>
    );
  }

  render() {
    const { idValidated } = this.props;

    if (!idValidated) {
      return null;
    }

    return (
      <div className="work-samples">
        { this.renderApproved() }
        { this.renderNonApproved() }
      </div>
    );
  }
}

WorkSamples.propTypes = {
  idValidated: PropTypes.bool.isRequired,
  idVerificationStatus: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  fetchIdPhotoIfNeeded: PropTypes.func.isRequired,
  fetchSampleGroups: PropTypes.func.isRequired,
  fetchWritingSamples: PropTypes.func.isRequired,
  fetchImageSamples: PropTypes.func.isRequired,
  setBannerError: PropTypes.func.isRequired,
  samples: PropTypes.object.isRequired,
  samplesMeta: PropTypes.object.isRequired,
  groups: PropTypes.array.isRequired,
};



// WEBPACK FOOTER //
// ./account/components/id_verification/work_samples.jsx